import React, { memo, useCallback, useMemo } from "react";
import Table, { Column } from "app/components/Table/Table";
import styles from "./TableUsers.module.scss";
import { useTranslation } from "react-i18next";
import {
  DashboardFlowUser,
  DashboardApiV2DashboardFlowUserDataGetRequest,
} from "@generated/v2";
import DashboardItemLayout from "../DashboardItemLayout/DashboardItemLayout";
import { DashboardItemFilterComplete } from "app/types/dashboardType";
import { useFetchData } from "app/hooks/hooks";
import { dashboardUserdataGet } from "app/api/v2/dashboardApi";
import { hasPermission } from "app/components/Permission/Permission";
import { PermissionEnum } from "app/constants/permissionConst";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import { TableProps } from "antd/lib/table";

interface Props extends Omit<DashboardItemFilterComplete, "original"> {}

const TableUsers = memo(
  ({ customerId, siteId, start, end, routes, period }: Props) => {
    const { t } = useTranslation();

    const { data, loading } = useFetchData<
      DashboardFlowUser[],
      DashboardApiV2DashboardFlowUserDataGetRequest
    >(dashboardUserdataGet, {
      shouldCallApi: !!customerId,
      params: {
        customerId: hasPermission([PermissionEnum.ACCOUNTS_READ])
          ? customerId
          : undefined,
        siteId,
        start,
        end,
        routes,
      },
    });

    const toggleExpandableClass = useCallback((row: DashboardFlowUser) => {
      return row.sharedUser ? "" : styles.notExpandable;
    }, []);

    const renderExpandedRow = useCallback(
      (row: DashboardFlowUser) => {
        return !!row.operators ? (
          <ErrorFallbackUI>
            <Table
              dataSource={row.operators}
              rowKey="operatorId"
              showHeader={false}
              pagination={false}
            >
              <Column
                title={t("dashboard.userCellOperator")}
                key="operatorId"
                dataIndex="operatorId"
                render={text => text}
                width={150}
              />
              <Column
                title={t("dashboard.userCellNoTests")}
                key="expandTestCount"
                dataIndex="testCount"
                render={text => text}
                defaultSortOrder="descend"
                sorter={(a: DashboardFlowUser, b: DashboardFlowUser) =>
                  (a.testCount || 0) - (b.testCount || 0)
                }
                width={130}
              />
              <Column
                title={t("dashboard.userCellNoPositive")}
                key="expandPositiveTestCount"
                dataIndex="positiveTestCount"
                sorter={(a: DashboardFlowUser, b: DashboardFlowUser) =>
                  (a.positiveTestCount || 0) - (b.positiveTestCount || 0)
                }
                render={text => text}
                width={130}
              />
              <Column
                title={t("dashboard.userCellPositiveControl")}
                key="expandPositiveControl"
                dataIndex="positiveControlCount"
                sorter={(a: DashboardFlowUser, b: DashboardFlowUser) =>
                  (a.positiveControlCount || 0) - (b.positiveControlCount || 0)
                }
                render={text => text}
                width={150}
              />
              <Column
                title={t("dashboard.userCellInconclusivePositive")}
                key="expandInconclusive"
                dataIndex="inconclusiveTestCount"
                sorter={(a: DashboardFlowUser, b: DashboardFlowUser) =>
                  (a.inconclusiveTestCount || 0) -
                  (b.inconclusiveTestCount || 0)
                }
                render={text => text}
                width={150}
              />
            </Table>
          </ErrorFallbackUI>
        ) : (
          ""
        );
      },
      [t]
    );

    const props = useMemo<TableProps<DashboardFlowUser>>(() => {
      return {
        dataSource: data,
        pagination: {
          defaultPageSize: 20,
        },
        rowClassName: toggleExpandableClass,
        expandedRowRender: renderExpandedRow,
        scroll: { x: 805 },
      };
    }, [data, renderExpandedRow, toggleExpandableClass]);

    return (
      <ErrorFallbackUI>
        <DashboardItemLayout
          title={t("dashboard.usersHeader")}
          info={t("dashboard.usersInfo")}
          subTitle={period}
          loading={loading}
        >
          <Table {...props}>
            <Column
              title={t("dashboard.user")}
              key="name"
              dataIndex="name"
              render={text => text}
              width={150}
            />
            <Column
              title={t("dashboard.userCellNoTests")}
              key="testCount"
              dataIndex="testCount"
              render={text => text}
              defaultSortOrder="descend"
              sorter={(a: DashboardFlowUser, b: DashboardFlowUser) =>
                (a.testCount || 0) - (b.testCount || 0)
              }
              width={130}
            />
            <Column
              title={t("dashboard.userCellNoPositive")}
              key="positiveTestCount"
              dataIndex="positiveTestCount"
              sorter={(a: DashboardFlowUser, b: DashboardFlowUser) =>
                (a.positiveTestCount || 0) - (b.positiveTestCount || 0)
              }
              render={text => text}
              width={130}
            />
            <Column
              title={t("dashboard.userCellPositiveControl")}
              key="positiveControl"
              dataIndex="positiveControlCount"
              sorter={(a: DashboardFlowUser, b: DashboardFlowUser) =>
                (a.positiveControlCount || 0) - (b.positiveControlCount || 0)
              }
              render={text => text}
              width={150}
            />
            <Column
              title={t("dashboard.userCellInconclusivePositive")}
              key="inconclusive"
              dataIndex="inconclusiveTestCount"
              sorter={(a: DashboardFlowUser, b: DashboardFlowUser) =>
                (a.inconclusiveTestCount || 0) - (b.inconclusiveTestCount || 0)
              }
              render={text => text}
              width={150}
            />
          </Table>
        </DashboardItemLayout>
      </ErrorFallbackUI>
    );
  }
);

export { TableUsers };
